import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { chatbotV2Service,
  type HotmartAccessCase,
  type HotmartClubConfig,
  type HotmartCommercialSignal,
  type HotmartProductMapping,
  type HotmartWebhookEvent,
  type IntegrationConnection,
  type IntegrationTool,
  type IntegrationToolExecution,
} from "../../services/chatbotV2/chatbotV2Service";
import {
  HOTMART_MEMBERS_IMPORT_URL,
  hotmartCommercialStatusLabel,
  hotmartConsentLabel,
  hotmartEligibilityLabel,
  hotmartProcessingStatusLabel,
  hotmartWebhookEventLabel,
} from "../../utils/hotmartClubUtils";
import { HotmartAccessCasesPanel } from "./HotmartAccessCasesPanel";


type WorkspaceTab = "overview" | "clubs" | "test" | "cases" | "approvals" | "events" | "actions";

const TABS: Array<{ key: WorkspaceTab; label: string }> = [
  { key: "overview", label: "Resumen" },
  { key: "clubs", label: "Clubs y entrega" },
  { key: "test", label: "Probar Hotmart" },
  { key: "cases", label: "Casos humanos" },
  { key: "approvals", label: "Aprobaciones" },
  { key: "events", label: "Tiempo real" },
  { key: "actions", label: "Capacidades" },
];

const DELIVERY_OPTIONS = [
  { value: "unknown", label: "Requiere clasificación" },
  { value: "hotmart_club", label: "Hotmart Club" },
  { value: "external", label: "Plataforma externa" },
  { value: "ebook", label: "Ebook / descarga" },
  { value: "external_event", label: "Evento / entrada" },
];

const PENDING_CASE_STATUSES = new Set([
  "open",
  "needs_review",
  "in_progress",
  "waiting_customer",
  "action_done_pending_verification",
]);

function errorMessage(error: unknown): string {
  const candidate = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = candidate?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return candidate?.message || "No se pudo completar la acción";
}

function statusPill(ok: boolean, label: string) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
      ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
    }`}>
      {ok ? <CheckCircle2 className="size-3.5" /> : <AlertTriangle className="size-3.5" />}
      {label}
    </span>
  );
}

function toolPolicyPill(tool: IntegrationTool) {
  if (!tool.enabled) return statusPill(false, "Desactivada");
  if (tool.policy === "approval_required") return statusPill(true, "Requiere aprobación");
  return statusPill(true, "Automática");
}

export function HotmartWorkspace({
  connection,
  tools,
  initialMappings,
  syncing,
  onSyncCatalog,
}: {
  connection: IntegrationConnection;
  tools: IntegrationTool[];
  initialMappings: HotmartProductMapping[];
  syncing: boolean;
  onSyncCatalog: () => Promise<void> | void;
}) {
  const [tab, setTab] = useState<WorkspaceTab>("overview");
  const [mappings, setMappings] = useState<HotmartProductMapping[]>(initialMappings);
  const [clubs, setClubs] = useState<HotmartClubConfig[]>([]);
  const [cases, setCases] = useState<HotmartAccessCase[]>([]);
  const [executions, setExecutions] = useState<IntegrationToolExecution[]>([]);
  const [events, setEvents] = useState<HotmartWebhookEvent[]>([]);
  const [signals, setSignals] = useState<HotmartCommercialSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingClubId, setEditingClubId] = useState<number | null>(null);
  const [clubForm, setClubForm] = useState({
    display_name: "",
    api_subdomain: "",
    public_url: "",
    product_mapping_ids: [] as number[],
  });
  const [clubTest, setClubTest] = useState<Record<string, unknown> | null>(null);
  const [testForm, setTestForm] = useState({ email: "", transaction: "", course_hint: "" });
  const [toolResult, setToolResult] = useState<IntegrationToolExecution | null>(null);

  useEffect(() => setMappings(initialMappings), [initialMappings]);

  const loadWorkspace = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [nextMappings, nextClubs, nextCases, nextExecutions, nextEvents, nextSignals] = await Promise.all([
        chatbotV2Service.listHotmartProductMappings(connection.id),
        chatbotV2Service.listHotmartClubs(connection.id),
        chatbotV2Service.listHotmartAccessCases({ limit: 200 }),
        chatbotV2Service.listIntegrationExecutions({ provider_key: "hotmart", limit: 200 }),
        chatbotV2Service.listHotmartWebhookEvents(connection.id),
        chatbotV2Service.listHotmartCommercialSignals(connection.id),
      ]);
      setMappings(nextMappings);
      setClubs(nextClubs);
      setCases(nextCases.filter((row) => row.connection_id === connection.id));
      setExecutions(nextExecutions.filter((row) => row.connection_id === connection.id));
      setEvents(nextEvents);
      setSignals(nextSignals);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [connection.id]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (tab !== "events") return;
    const intervalId = window.setInterval(() => {
      void loadWorkspace(false);
    }, 15_000);
    return () => window.clearInterval(intervalId);
  }, [loadWorkspace, tab]);

  const activeMappings = useMemo(
    () => mappings.filter((row) => row.hotmart_status === "ACTIVE" || row.is_active),
    [mappings]
  );
  const clubFormMappings = useMemo(() => {
    const editingProductIds = new Set(
      clubs.find((club) => club.id === editingClubId)?.product_mapping_ids || []
    );
    return mappings.filter((mapping) => {
      const isActive = mapping.hotmart_status === "ACTIVE" || mapping.is_active;
      const belongsToEditingClub = editingProductIds.has(mapping.id);
      const belongsToAnotherClub = Boolean(mapping.club_config_id) && !belongsToEditingClub;
      return !belongsToAnotherClub && (isActive || belongsToEditingClub);
    });
  }, [clubs, editingClubId, mappings]);
  const configuredActive = activeMappings.filter((row) => row.delivery_type !== "unknown");
  const pendingCases = cases.filter((row) => PENDING_CASE_STATUSES.has(row.case_status));
  const pendingApprovals = executions.filter((row) => row.status === "pending_approval");
  const failedEvents = events.filter((row) => ["retry_scheduled", "dead_letter", "failed"].includes(row.processing_status));
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    mappings.forEach((row) => {
      const key = row.hotmart_status || "UNKNOWN";
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [mappings]);

  const resetClubForm = () => {
    setEditingClubId(null);
    setClubForm({ display_name: "", api_subdomain: "", public_url: "", product_mapping_ids: [] });
    setClubTest(null);
  };

  const editClub = (club: HotmartClubConfig) => {
    setEditingClubId(club.id);
    setClubForm({
      display_name: club.display_name,
      api_subdomain: club.api_subdomain || "",
      public_url: club.public_url || "",
      product_mapping_ids: club.product_mapping_ids || [],
    });
    setClubTest(club.last_test_status === "ok" ? { ok: true, status: "ok", api_subdomain: club.api_subdomain } : null);
  };

  const testClub = async () => {
    if (!clubForm.api_subdomain.trim()) return;
    setBusy("club-test");
    try {
      const result = await chatbotV2Service.testHotmartClub(connection.id, {
        api_subdomain: clubForm.api_subdomain.trim(),
      });
      setClubTest(result);
      if (result.ok) toast.success("Hotmart permitió consultar este Club");
      else toast.error(String(result.message || "Hotmart rechazó este dato"));
    } catch (error) {
      setClubTest({ ok: false, message: errorMessage(error) });
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const saveClub = async () => {
    if (!clubTest?.ok || clubTest.api_subdomain !== clubForm.api_subdomain.trim().toLowerCase()) {
      toast.error("Prueba este subdominio exacto antes de guardar");
      return;
    }
    setBusy("club-save");
    try {
      const payload = {
        display_name: clubForm.display_name.trim(),
        api_subdomain: clubForm.api_subdomain.trim(),
        public_url: clubForm.public_url.trim() || null,
        product_mapping_ids: clubForm.product_mapping_ids,
      };
      if (editingClubId) await chatbotV2Service.updateHotmartClub(editingClubId, payload);
      else await chatbotV2Service.createHotmartClub(connection.id, payload);
      toast.success(editingClubId ? "Club actualizado y probado" : "Club configurado y probado");
      resetClubForm();
      await loadWorkspace();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const deleteClub = async (club: HotmartClubConfig) => {
    if (!window.confirm(`¿Eliminar la configuración de ${club.display_name}? Sus productos quedarán por clasificar.`)) return;
    setBusy(`club-${club.id}`);
    try {
      await chatbotV2Service.deleteHotmartClub(club.id);
      toast.success("Configuración de Club eliminada");
      await loadWorkspace();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const updateDelivery = async (mapping: HotmartProductMapping, delivery_type: string) => {
    setBusy(`mapping-${mapping.id}`);
    try {
      const updated = await chatbotV2Service.updateHotmartProductMapping(mapping.id, { delivery_type });
      setMappings((current) => current.map((row) => row.id === updated.id ? updated : row));
      toast.success("Tipo de entrega actualizado");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const updatePedagogicalSupport = async (mapping: HotmartProductMapping, enabled: boolean) => {
    setBusy(`mapping-${mapping.id}`);
    try {
      const updated = await chatbotV2Service.updateHotmartProductMapping(mapping.id, {
        metadata_json: { support_pedagogical_enabled: enabled },
      });
      setMappings((current) => current.map((row) => row.id === updated.id ? updated : row));
      toast.success(enabled ? "Soporte pedagógico habilitado" : "Soporte pedagógico deshabilitado");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const runSafeTool = async (toolName: string) => {
    setBusy(`tool-${toolName}`);
    setToolResult(null);
    try {
      const execution = await chatbotV2Service.executeIntegrationTool(connection.id, toolName, {
        actor_type: "owner",
        inputs: {
          email: testForm.email.trim() || undefined,
          transaction: testForm.transaction.trim().toUpperCase() || undefined,
          course_hint: testForm.course_hint.trim() || undefined,
        },
      });
      setToolResult(execution);
      if (execution.status === "succeeded") toast.success("Consulta completada con datos nuevos");
      else toast.error(execution.error_message_sanitized || "La consulta no se completó");
      await loadWorkspace();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const updateCase = async (
    accessCase: HotmartAccessCase,
    patch: Partial<Pick<HotmartAccessCase, "case_status" | "resolution_note">>
  ) => {
    setBusy(`case-${accessCase.id}`);
    try {
      const updated = await chatbotV2Service.updateHotmartAccessCase(accessCase.id, patch);
      setCases((current) => current.map((row) => row.id === updated.id ? updated : row));
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const verifyCase = async (accessCase: HotmartAccessCase) => {
    setBusy(`case-${accessCase.id}`);
    try {
      const updated = await chatbotV2Service.verifyHotmartAccessCase(accessCase.id);
      setCases((current) => current.map((row) => row.id === updated.id ? updated : row));
      toast.success(updated.case_status === "resolved" ? "Hotmart confirmó el acceso" : "Reverificado; todavía necesita revisión");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const decideApproval = async (execution: IntegrationToolExecution, approve: boolean) => {
    setBusy(`approval-${execution.id}`);
    try {
      if (approve) {
        const result = await chatbotV2Service.approveIntegrationExecution(execution.id);
        if (result.execution.status === "succeeded") toast.success("Acción aprobada y ejecutada");
        else toast.error(result.execution.error_message_sanitized || "La acción aprobada falló");
      } else {
        await chatbotV2Service.rejectIntegrationExecution(execution.id, "Rechazada por el owner");
        toast.success("Acción rechazada; no se modificó Hotmart");
      }
      await loadWorkspace();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const retryEvent = async (event: HotmartWebhookEvent) => {
    setBusy(`event-${event.id}`);
    try {
      await chatbotV2Service.retryHotmartWebhookEvent(event.id);
      toast.success("Evento reprocesado");
      await loadWorkspace();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 pt-4">
        <div className="flex gap-1 overflow-x-auto pb-3">
          {TABS.map((item) => {
            const badge = item.key === "cases" ? pendingCases.length : item.key === "approvals" ? pendingApprovals.length : item.key === "events" ? failedEvents.length : 0;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold ${tab === item.key ? "bg-gray-950 text-white" : "text-gray-600 hover:bg-gray-100"}`}
              >
                {item.label}{badge ? ` · ${badge}` : ""}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-64 items-center justify-center"><Loader2 className="size-6 animate-spin text-gray-400" /></div>
      ) : null}

      {!loading && tab === "overview" ? (
        <div className="space-y-6 p-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-gray-200 p-4"><p className="text-xs text-gray-500">Catálogo</p><p className="mt-1 text-2xl font-semibold">{mappings.length}</p><p className="mt-1 text-xs text-gray-500">{statusCounts.ACTIVE || 0} activos · {statusCounts.PAUSED || 0} pausados · {statusCounts.DRAFT || 0} borrador</p></div>
            <div className="rounded-xl border border-gray-200 p-4"><p className="text-xs text-gray-500">Cobertura de entrega</p><p className="mt-1 text-2xl font-semibold">{configuredActive.length}/{activeMappings.length}</p><p className="mt-1 text-xs text-gray-500">productos activos clasificados</p></div>
            <div className="rounded-xl border border-gray-200 p-4"><p className="text-xs text-gray-500">Casos pendientes</p><p className="mt-1 text-2xl font-semibold">{pendingCases.length}</p><p className="mt-1 text-xs text-gray-500">con diagnóstico y evidencia</p></div>
            <div className="rounded-xl border border-gray-200 p-4"><p className="text-xs text-gray-500">Eventos con fallo</p><p className="mt-1 text-2xl font-semibold">{failedEvents.length}</p><p className="mt-1 text-xs text-gray-500">reintentables o dead-letter</p></div>
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-xl bg-gray-50 p-4"><p className="text-sm font-semibold">OAuth y credenciales</p><div className="mt-2">{statusPill(connection.diagnostic_status === "ok", connection.diagnostic_status === "ok" ? "Operativo" : "Pendiente")}</div></div>
            <div className="rounded-xl bg-gray-50 p-4"><p className="text-sm font-semibold">Catálogo</p><div className="mt-2">{statusPill(Boolean(connection.last_synced_at), connection.last_synced_at ? "Sincronizado" : "Sin sincronizar")}</div></div>
            <div className="rounded-xl bg-gray-50 p-4"><p className="text-sm font-semibold">Clubs</p><div className="mt-2">{statusPill(clubs.some((row) => row.last_test_status === "ok"), `${clubs.filter((row) => row.last_test_status === "ok").length} probados`)}</div></div>
            <div className="rounded-xl bg-gray-50 p-4"><p className="text-sm font-semibold">Webhooks</p><div className="mt-2">{statusPill(Boolean(connection.secret_status.hottok), connection.secret_status.hottok ? "Hottok configurado" : "Hottok obligatorio")}</div></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={syncing} onClick={() => void onSyncCatalog()} className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white disabled:opacity-50">{syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}Sincronizar todas las páginas del catálogo</button>
            <button type="button" onClick={() => setTab("clubs")} className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-800">Configurar Clubs <ChevronRight className="size-4" /></button>
            <button type="button" onClick={() => setTab("test")} className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-800">Probar diagnóstico <Play className="size-4" /></button>
          </div>
        </div>
      ) : null}

      {!loading && tab === "clubs" ? (
        <div className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(340px,0.8fr)]">
          <div className="space-y-4">
            <div><h3 className="text-base font-semibold text-gray-950">Clubs configurados</h3><p className="mt-1 text-sm text-gray-500">Un Club se configura una vez y puede agrupar varios productos. El acceso al Club no prueba por sí solo el producto en Clubs multiproducto.</p></div>
            {clubs.map((club) => (
              <div key={club.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3"><div><p className="font-semibold text-gray-950">{club.display_name}</p><p className="mt-1 text-xs text-gray-500">Subdominio API: {club.api_subdomain || "sin configurar"}</p><p className="mt-1 text-xs text-gray-500">URL del alumno: {club.public_url || "sin URL"}</p></div>{statusPill(club.last_test_status === "ok", club.last_test_status === "ok" ? "API probada" : club.last_test_status || "Sin probar")}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">{club.product_mapping_ids.map((id) => <span key={id} className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">{mappings.find((row) => row.id === id)?.product_name || `Producto ${id}`}</span>)}</div>
                <div className="mt-3 flex gap-2"><button type="button" onClick={() => editClub(club)} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold">Editar y probar</button><button type="button" disabled={busy === `club-${club.id}`} onClick={() => void deleteClub(club)} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-red-600"><Trash2 className="size-3.5" />Eliminar</button></div>
              </div>
            ))}
            {!clubs.length ? <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">Todavía no hay Clubs configurados. Usa la guía de la derecha; no adivines el subdominio.</div> : null}
            <div className="rounded-xl border border-gray-200">
              <div className="border-b border-gray-100 px-4 py-3"><p className="font-semibold text-gray-950">Clasificación de productos activos</p><p className="mt-1 text-xs text-gray-500">Los estados ACTIVE, PAUSED y DRAFT vienen de Hotmart. Aquí solo declaras cómo se entrega cada producto.</p></div>
              <div className="divide-y divide-gray-100">{activeMappings.map((mapping) => <div key={mapping.id} className="grid gap-2 px-4 py-3 sm:grid-cols-[1fr_220px_180px]"><div><p className="text-sm font-semibold">{mapping.product_name}</p><p className="text-xs text-gray-500">{mapping.hotmart_status || "UNKNOWN"} · {mapping.product_id}</p>{mapping.club_config_id && mapping.delivery_type !== "hotmart_club" ? <p className="mt-1 text-[11px] text-amber-700">Hotmart lo lista en un Club para verificar alumno, pero el contenido se entrega por {DELIVERY_OPTIONS.find((row) => row.value === mapping.delivery_type)?.label.toLowerCase()}.</p> : null}</div><select value={mapping.delivery_type} disabled={busy === `mapping-${mapping.id}`} onChange={(event) => void updateDelivery(mapping, event.target.value)} className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm"><option value="unknown">Requiere clasificación</option>{DELIVERY_OPTIONS.filter((row) => row.value !== "unknown" && (row.value !== "hotmart_club" || Boolean(mapping.club_config_id))).map((row) => <option key={row.value} value={row.value}>{row.label}</option>)}</select><label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={Boolean(mapping.metadata_json.support_pedagogical_enabled)} disabled={busy === `mapping-${mapping.id}`} onChange={(event) => void updatePedagogicalSupport(mapping, event.target.checked)} />Responder conceptos solo con conocimiento cargado</label></div>)}</div>
            </div>
          </div>
          <div className="h-fit rounded-xl border border-gray-200 bg-gray-50 p-4 lg:sticky lg:top-4">
            <div className="flex items-center justify-between"><div><p className="font-semibold text-gray-950">{editingClubId ? "Editar Club" : "Configurar un Club"}</p><p className="mt-1 text-xs text-gray-500">La prueba usa la API real antes de guardar.</p></div>{editingClubId ? <button type="button" onClick={resetClubForm} className="text-xs font-semibold text-gray-500">Cancelar</button> : <Plus className="size-5 text-gray-500" />}</div>
            <ol className="mt-4 space-y-2 rounded-xl bg-white p-3 text-xs leading-5 text-gray-700">
              <li><strong>1.</strong> Abre Hotmart y entra a la cuenta/productor correcto. Si eres colaborador: cambia a la Central correspondiente.</li>
              <li><strong>2.</strong> Ve a <strong>Productos → Mis productos</strong> y abre cualquier producto de ese Club.</li>
              <li><strong>3.</strong> Pulsa <strong>Acceder a la gestión del curso</strong>.</li>
              <li><strong>4.</strong> Abre <strong>Configuraciones → Integraciones</strong>.</li>
              <li><strong>5.</strong> En <strong>Subdominio</strong>, pulsa <strong>Copiar subdominio</strong> y pega aquí solo ese valor.</li>
            </ol>
            <div className="mt-4 space-y-3">
              <label className="block text-xs font-semibold text-gray-700">Nombre para reconocer el Club<input value={clubForm.display_name} onChange={(event) => setClubForm((current) => ({ ...current, display_name: event.target.value }))} placeholder="Ej. Protocolo IA" className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal" /></label>
              <label className="block text-xs font-semibold text-gray-700">Subdominio API exacto<input value={clubForm.api_subdomain} onChange={(event) => { setClubForm((current) => ({ ...current, api_subdomain: event.target.value })); setClubTest(null); }} placeholder="Ej. protocoloia-yaxkwn" className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal" /><span className="mt-1 block font-normal text-amber-700">No uses el slug de la URL pública. Por ejemplo, “protocolo-ia” y “protocoloia-yaxkwn” pueden ser distintos.</span></label>
              <label className="block text-xs font-semibold text-gray-700">URL pública para el alumno (opcional)<input value={clubForm.public_url} onChange={(event) => setClubForm((current) => ({ ...current, public_url: event.target.value }))} placeholder="https://hotmart.com/ES/club/..." className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal" /></label>
              <div><p className="text-xs font-semibold text-gray-700">Productos de este Club</p><p className="mt-1 text-xs text-gray-500">Al editar, también mostramos productos pausados o borrador que ya estaban asignados para que puedas retirarlos sin quedar atrapado con una configuración vieja.</p><div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2">{clubFormMappings.map((mapping) => <label key={mapping.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs hover:bg-gray-50"><input type="checkbox" checked={clubForm.product_mapping_ids.includes(mapping.id)} onChange={(event) => setClubForm((current) => ({ ...current, product_mapping_ids: event.target.checked ? [...current.product_mapping_ids, mapping.id] : current.product_mapping_ids.filter((id) => id !== mapping.id) }))} />{mapping.product_name}{mapping.hotmart_status !== "ACTIVE" ? <span className="ml-auto text-[10px] font-semibold text-amber-700">{mapping.hotmart_status || "INACTIVO"}</span> : null}</label>)}</div></div>
              {clubTest ? <div className={`rounded-xl p-3 text-sm ${clubTest.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>{clubTest.ok ? <CheckCircle2 className="mr-2 inline size-4" /> : <XCircle className="mr-2 inline size-4" />}{String(clubTest.message || (clubTest.ok ? "Prueba correcta" : "Prueba fallida"))}</div> : null}
              <div className="flex gap-2"><button type="button" disabled={!clubForm.api_subdomain.trim() || busy === "club-test"} onClick={() => void testClub()} className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold disabled:opacity-50">{busy === "club-test" ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}Probar con Hotmart</button><button type="button" disabled={!clubForm.display_name.trim() || !clubTest?.ok || busy === "club-save"} onClick={() => void saveClub()} className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white disabled:opacity-50">{busy === "club-save" ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}Guardar</button></div>
            </div>
          </div>
        </div>
      ) : null}

      {!loading && tab === "test" ? (
        <div className="grid gap-5 p-5 lg:grid-cols-[360px_1fr]">
          <div className="space-y-3"><div><h3 className="font-semibold text-gray-950">Prueba segura y con datos frescos</h3><p className="mt-1 text-sm text-gray-500">Estas consultas no crean casos ni modifican Hotmart. Usa email, HP o ambos.</p></div><input value={testForm.email} onChange={(event) => setTestForm((current) => ({ ...current, email: event.target.value }))} placeholder="Correo usado en la compra" className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm" /><input value={testForm.transaction} onChange={(event) => setTestForm((current) => ({ ...current, transaction: event.target.value }))} placeholder="Código HP (opcional)" className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm" /><input value={testForm.course_hint} onChange={(event) => setTestForm((current) => ({ ...current, course_hint: event.target.value }))} placeholder="Curso o producto (opcional)" className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm" /><div className="grid gap-2"><button type="button" disabled={!testForm.email.trim() && !testForm.transaction.trim()} onClick={() => void runSafeTool("hotmart_resolve_access")} className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gray-950 text-sm font-semibold text-white disabled:opacity-50"><Play className="size-4" />Diagnosticar acceso</button><button type="button" disabled={!testForm.email.trim()} onClick={() => void runSafeTool("hotmart_course_progress")} className="h-10 rounded-full bg-gray-100 text-sm font-semibold disabled:opacity-50">Consultar progreso</button><button type="button" disabled={!testForm.email.trim() && !testForm.transaction.trim()} onClick={() => void runSafeTool("hotmart_subscription_status")} className="h-10 rounded-full bg-gray-100 text-sm font-semibold disabled:opacity-50">Consultar suscripción</button></div></div>
          <div className="min-h-72 rounded-xl border border-gray-200 bg-gray-950 p-4 text-sm text-gray-100">{busy?.startsWith("tool-") ? <div className="flex h-full items-center justify-center"><Loader2 className="size-6 animate-spin" /></div> : toolResult ? <><div className="mb-3 flex items-center justify-between"><span className="font-semibold">Resultado · {toolResult.status}</span><span className="text-xs text-gray-400">Ejecución #{toolResult.id}</span></div><pre className="overflow-auto whitespace-pre-wrap text-xs leading-5">{JSON.stringify(toolResult.output_summary_json, null, 2)}</pre></> : <div className="flex h-full items-center justify-center text-center text-gray-400">El resultado mostrará compra, producto, Club, suscripción y siguiente acción por separado.</div>}</div>
        </div>
      ) : null}

      {!loading && tab === "cases" ? <div className="p-5"><HotmartAccessCasesPanel cases={cases} loading={false} actionLoadingId={busy?.startsWith("case-") ? Number(busy.replace("case-", "")) : null} onRefresh={() => void loadWorkspace()} onUpdateCase={(accessCase, patch) => void updateCase(accessCase, patch)} onVerifyCase={(accessCase) => void verifyCase(accessCase)} /></div> : null}

      {!loading && tab === "approvals" ? (
        <div className="space-y-4 p-5"><div><h3 className="font-semibold text-gray-950">Acciones sensibles pendientes</h3><p className="mt-1 text-sm text-gray-500">Cancelar, reactivar, cambiar cobro, reembolsar o crear descuentos nunca se ejecuta desde una frase ambigua. Revisa el resumen y decide.</p></div>{pendingApprovals.map((execution) => <div key={execution.id} className="rounded-xl border border-gray-200 p-4"><div className="flex flex-wrap items-start justify-between gap-3"><div><p className="font-semibold text-gray-950">{tools.find((tool) => tool.tool_name === execution.tool_name)?.display_name || execution.tool_name}</p><p className="mt-1 text-xs text-gray-500">Solicitud #{execution.id} · {execution.created_at ? new Date(execution.created_at).toLocaleString() : ""}</p><pre className="mt-3 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs text-gray-700">{JSON.stringify(execution.input_summary_json || execution.output_summary_json, null, 2)}</pre></div><div className="flex gap-2"><button type="button" disabled={busy === `approval-${execution.id}`} onClick={() => void decideApproval(execution, false)} className="h-9 rounded-full bg-gray-100 px-3 text-sm font-semibold">Rechazar</button><button type="button" disabled={busy === `approval-${execution.id}`} onClick={() => void decideApproval(execution, true)} className="h-9 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white">Aprobar y ejecutar</button></div></div></div>)}{!pendingApprovals.length ? <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">No hay acciones esperando aprobación.</div> : null}</div>
      ) : null}

      {!loading && tab === "events" ? (
        <div className="space-y-4 p-5"><div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="font-semibold text-gray-950">Eventos Hotmart y sincronización</h3><p className="mt-1 text-sm text-gray-500">Un webhook dispara una reverificación contra la API; no reemplaza la verdad de Hotmart. Esta vista se actualiza automáticamente cada 15 segundos.</p></div><div className="flex items-center gap-2">{statusPill(Boolean(connection.secret_status.hottok), connection.secret_status.hottok ? "Hottok protegido" : "Configura Hottok para recibir eventos")}<button type="button" onClick={() => void loadWorkspace(false)} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-gray-100 px-3 text-xs font-semibold text-gray-700"><RefreshCw className="size-3.5" />Recargar eventos</button></div></div><div className="overflow-hidden rounded-xl border border-gray-200"><table className="w-full text-left text-sm"><thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr><th className="px-4 py-3">Evento</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Recibido</th><th className="px-4 py-3"></th></tr></thead><tbody className="divide-y divide-gray-100">{events.map((event) => <tr key={event.id}><td className="px-4 py-3"><p className="font-semibold">{hotmartWebhookEventLabel(event.event_type)}</p><p className="text-xs text-gray-500">{event.transaction || event.subscription_code || event.product_id || event.hotmart_event_id}</p></td><td className="px-4 py-3">{statusPill(event.processing_status === "processed", hotmartProcessingStatusLabel(event.processing_status))}{event.processing_error ? <p className="mt-1 max-w-xs text-xs text-red-600">{event.processing_error}</p> : null}</td><td className="px-4 py-3 text-xs text-gray-500">{event.received_at ? new Date(event.received_at).toLocaleString() : "—"}</td><td className="px-4 py-3 text-right">{event.processing_status !== "processed" ? <button type="button" disabled={busy === `event-${event.id}`} onClick={() => void retryEvent(event)} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold">Reintentar</button> : null}</td></tr>)}</tbody></table>{!events.length ? <div className="p-10 text-center text-sm text-gray-500">Todavía no hay eventos recibidos.</div> : null}</div><div><h4 className="font-semibold text-gray-950">Señales de recuperación comercial</h4><p className="mt-1 text-sm text-gray-500">Fluxi nunca contacta un carrito Hotmart sin consentimiento explícito. Opt-out y ausencia de consentimiento bloquean el envío.</p><div className="mt-3 grid gap-2 md:grid-cols-2">{signals.map((signal) => <div key={signal.id} className="rounded-xl border border-gray-200 p-3"><div className="flex items-start justify-between gap-2"><div><p className="text-sm font-semibold">{hotmartWebhookEventLabel(signal.signal_type.toUpperCase())}</p><p className="mt-1 text-xs text-gray-500">Producto {mappings.find((row) => row.product_id === signal.product_id)?.product_name || signal.product_id || "sin identificar"}</p></div>{statusPill(signal.status === "recovered", hotmartCommercialStatusLabel(signal.status))}</div><p className="mt-2 text-xs text-gray-500">{hotmartConsentLabel(signal.consent_status)} · {hotmartEligibilityLabel(signal.eligibility_reason)}</p></div>)}{!signals.length ? <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 md:col-span-2">No hay señales comerciales recibidas.</p> : null}</div></div></div>
      ) : null}

      {!loading && tab === "actions" ? (
        <div className="space-y-4 p-5"><div><h3 className="font-semibold text-gray-950">Capacidades orientadas a casos de uso</h3><p className="mt-1 text-sm text-gray-500">Las consultas técnicas de ventas, buyer y Club viven dentro de “Resolver acceso”; no aparecen como features duplicados.</p></div><div className="grid gap-3 md:grid-cols-2">{tools.map((tool) => <div key={tool.tool_name} className="rounded-xl border border-gray-200 p-4"><div className="flex items-start justify-between gap-2"><div><p className="font-semibold text-gray-950">{tool.display_name}</p><p className="mt-1 text-sm leading-5 text-gray-500">{tool.description}</p></div>{toolPolicyPill(tool)}</div></div>)}</div><a href={HOTMART_MEMBERS_IMPORT_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">Abrir alumnos en Hotmart para una acción manual <ExternalLink className="size-4" /></a></div>
      ) : null}
    </section>
  );
}
